<template>
  <div v-if="disabled" class="PanelDropdown-trigger">
    <slot name="trigger" />
  </div>

  <PopoverRoot v-else v-model:open="isOpen" :modal>
    <PopoverTrigger asChild class="PanelDropdown-trigger" :class="$attrs.class">
      <slot name="trigger" />
    </PopoverTrigger>

    <PopoverPortal>
      <PopoverContent
        :side
        :align
        :sideOffset
        avoidCollisions
        class="PanelDropdown-content"
        :class="{ 'PanelDropdown-content--anchorWidth': inheritWidth }"
        @escape-key-down="closePopover"
        @pointer-down-outside="closePopover"
      >
        <template v-if="asChild">
          <slot />
        </template>
        <Card
          v-else
          elevation
          class="PanelDropdown-card"
          :class="cardClass"
        >
          <slot />
        </Card>
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>

<script setup lang="ts">
import {
  PopoverRoot,
  PopoverTrigger,
  PopoverPortal,
  PopoverContent,
} from 'reka-ui';
import Card from '../../atoms/Card/Card.vue';
import type { PanelDropdownProps } from './PanelDropdown.model';

withDefaults(defineProps<PanelDropdownProps>(), {
  side: 'bottom',
  align: 'center',
  sideOffset: 8,
  modal: false,
});

const isOpen = defineModel<boolean>();

function closePopover() {
  isOpen.value = false;
}
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/animations';

@layer cvo-ui {
  .PanelDropdown {
    &-content {
      transform-origin: var(--reka-popover-content-transform-origin);
      animation: scaleIn vars.$transition-fast ease forwards;
      box-shadow: vars.$shadow-md;

      background-color: vars.$organisms-panel-dropdown-bg-color;
      border: vars.$organisms-panel-dropdown-border-stroke solid vars.$organisms-panel-dropdown-border-color;
      border-radius: vars.$organisms-panel-dropdown-border-radius;

      // Avoids the panel to go beyond the viewport bottom
      max-height: var(--reka-popper-available-height);
      overflow-y: auto;

      &--anchorWidth {
        width: var(--reka-popper-anchor-width);
        min-width: min-content;
        .PanelDropdown-card {
          max-width: unset;
        }
      }
    }

    &-card {
      padding: vars.$dimensions-d-4;
      max-width: 400px;
    }
  }
}
</style>